import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, FileText, BarChart3, Presentation, Users, Cloud, Share2, Calendar, Bot, Newspaper, Clock, ArrowRight, CheckCircle, Star, TrendingUp, Video, Search } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import Breadcrumb from './Breadcrumb';
import Footer from './Footer';

const GoogleWorkspacePage = ({ setCurrentSection, onSelectGuide, guides }) => {
  const navigate = useNavigate();
  
  // Function to count procedures in a guide by analyzing internal links
  const countProceduresInGuide = (guideId) => {
    const guide = guides.find(g => g.id === guideId);
    if (!guide || !guide.content || !guide.content.sections) return 0;
    
    let linkCount = 0;
    guide.content.sections.forEach(section => {
      if (section.content) {
        // Count links in format [text](link-id)
        const links = section.content.match(/\[([^\]]+)\]\(([^)]+)\)/g);
        if (links) {
          linkCount += links.length;
        }
      }
    });
    return linkCount;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-100">
      {/* Breadcrumb */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb />
        </div>
      </div>

      {/* Hero Banner */}
      <section className="relative overflow-hidden bg-gradient-to-r from-green-600 via-blue-600 to-purple-600">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white bg-opacity-20 backdrop-blur-sm rounded-3xl mb-8">
              <Search className="h-12 w-12 text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-6">
              Google Workspace
            </h1>
            <p className="text-xl md:text-2xl text-green-100 max-w-3xl mx-auto">
              Maîtrisez la suite Google Workspace : Gmail, Drive, Docs et tous les outils collaboratifs
            </p>
          </div>
        </div>
        
        {/* Floating elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-green-300 rounded-full opacity-20 animate-bounce"></div>
        <div className="absolute bottom-20 right-10 w-16 h-16 bg-blue-300 rounded-full opacity-30 animate-pulse"></div>
        <div className="absolute top-1/2 left-1/4 w-12 h-12 bg-purple-300 rounded-full opacity-25 animate-ping"></div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Section Nouveau sur Google Workspace */}
        <section className="mb-16 bg-gradient-to-r from-green-50 to-blue-50 rounded-3xl p-8 shadow-lg">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-green-500 to-blue-600 rounded-3xl mb-8 animate-pulse shadow-2xl">
              <Search className="h-12 w-12 text-white" />
            </div>
            <h2 className="text-4xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-6">
              Nouveau sur Google Workspace ?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Découvrez les outils Google et leur écosystème collaboratif intégré
            </p>
            <Button 
              className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white font-bold px-8 py-4 rounded-full text-lg shadow-lg transform hover:scale-105 transition-all duration-300"
              onClick={() => navigate('/google-workspace/presentation')}
            >
              <Presentation className="h-6 w-6 mr-3" />
              Découvrir Google Workspace en slides
              <ArrowRight className="ml-3 h-6 w-6" />
            </Button>
          </div>
        </section>

        {/* Tools Section */}
        <section className="bg-gradient-to-br from-yellow-50 to-red-50 rounded-3xl p-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-yellow-400 to-red-500 rounded-2xl mb-6">
              <Bot className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Guides par Outil
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Maîtrisez chaque application Google Workspace avec nos guides détaillés
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                name: 'Gmail',
                icon: Mail,
                description: 'Gestion avancée des emails et organisation',
                gradient: 'from-red-500 to-red-600',
                guideId: 'gmail-organization',
                hasGuides: true
              },
              {
                name: 'Google Docs',
                icon: FileText,
                description: 'Rédaction collaborative et mise en forme',
                gradient: 'from-blue-500 to-blue-600',
                guideId: 'google-docs',
                hasGuides: true
              },
              {
                name: 'Google Sheets',
                icon: BarChart3,
                description: 'Tableaux et analyses de données',
                gradient: 'from-green-500 to-green-600',
                guideId: 'google-sheets',
                hasGuides: true
              },
              {
                name: 'Google Slides',
                icon: Presentation,
                description: 'Présentations dynamiques et interactives',
                gradient: 'from-orange-500 to-orange-600',
                guideId: 'google-slides',
                hasGuides: true
              },
              {
                name: 'Google Meet',
                icon: Video,
                description: 'Visioconférences et collaboration',
                gradient: 'from-teal-500 to-teal-600',
                guideId: 'google-meet',
                hasGuides: true
              },
              {
                name: 'Google Drive',
                icon: Cloud,
                description: 'Stockage et synchronisation de fichiers',
                gradient: 'from-yellow-500 to-yellow-600',
                guideId: 'google-drive',
                hasGuides: true
              },
              {
                name: 'Google Calendar',
                icon: Calendar,
                description: 'Gestion du temps et planification',
                gradient: 'from-purple-500 to-purple-600',
                guideId: 'google-calendar',
                hasGuides: true
              }
            ].map((tool, index) => {
              const IconComponent = tool.icon;
              const procedureCount = tool.hasGuides ? countProceduresInGuide(tool.guideId) : 0;
              
              return (
                <Card 
                  key={index} 
                  className="group cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 border-0 overflow-hidden relative h-full flex flex-col"
                  onClick={() => {
                    if (tool.hasGuides && onSelectGuide) {
                      console.log('Guide/Item selected:', { id: tool.guideId });
                      onSelectGuide({ id: tool.guideId });
                    }
                  }}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${tool.gradient} opacity-5 group-hover:opacity-10 transition-opacity duration-300`}></div>
                  
                  <CardHeader className="text-center pb-4 relative z-10 flex-grow">
                    <div className={`mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br ${tool.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900 mb-2">
                      {tool.name}
                    </CardTitle>
                    <CardDescription className="text-gray-600 leading-relaxed flex-grow">
                      {tool.description}
                    </CardDescription>
                    
                    {tool.hasGuides && procedureCount > 0 && (
                      <div className="mt-4 flex items-center justify-center">
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          {procedureCount} procédure{procedureCount > 1 ? 's' : ''}
                        </Badge>
                      </div>
                    )}
                    
                    {tool.hasGuides && procedureCount === 0 && (
                      <div className="mt-4 flex items-center justify-center">
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          1 guide
                        </Badge>
                      </div>
                    )}
                  </CardHeader>
                  
                  {/* Bottom Action Bar */}
                  <div className={`bg-gray-900 ${tool.hasGuides ? `group-hover:bg-gradient-to-r group-hover:${tool.gradient}` : ''} transition-all duration-300 p-4 relative z-10`}>
                    <Button 
                      variant="ghost"
                      disabled={!tool.hasGuides}
                      className={`w-full font-bold text-lg hover:bg-transparent ${
                        tool.hasGuides 
                          ? 'text-white' 
                          : 'text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {tool.hasGuides ? 'Consulter le guide' : 'Prochainement'}
                      {tool.hasGuides && <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />}
                    </Button>
                  </div>
                  
                  {tool.hasGuides && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-1000 pointer-events-none"></div>
                  )}
                </Card>
              );
            })}
          </div>
        </section>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default GoogleWorkspacePage;